/**
 * SEO HEADING STRUCTURE - Study With Friends
 * ==========================================
 * 
 * TITLE: Study With Friends | CSW | Online Workspace | Productivity Tool | Study Stream
 * META DESCRIPTION: Create private study rooms with friends...
 * 
 * H1 (1 tag):
 *   - "Study With Friends" (hero section)
 * 
 * H2 (3 tags):
 *   - "Group Study Features" (features section)
 *   - "Why Study With Friends?" (benefits section)
 *   - "Ready to Study Together?" (CTA section)
 * 
 * H3 (8 tags):
 *   - Feature card titles (4): "Private Rooms", "Video Chat", etc.
 *   - Benefit titles (4): Dynamic benefit items
 */

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Lock, Video, MessageCircle, Play, ArrowRight } from "lucide-react";

const StudyWithFriends = () => {
  const features = [
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Private Rooms",
      description: "Create invite-only study rooms for your friend group",
    },
    {
      icon: <Video className="w-8 h-8" />,
      title: "Video Chat",
      description: "Stay connected with optional video and audio",
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Group Chat",
      description: "Communicate without breaking focus",
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Shared Goals",
      description: "See what everyone's working on and stay motivated together",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        title="Study With Friends - Private Study Rooms"
        description="Create private study rooms with friends. Video chat, group messaging, shared goals, and invite-only access. Study together online anytime."
        keywords="study with friends, private study room, group study online, study together, friend study room, online study group"
        canonicalUrl="/study-with-friends/"
      />
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-5xl mx-auto text-center space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
                <Users className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">PRIVATE STUDY ROOMS</span>
              </div>
              
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <span className="text-gradient">Study With Friends</span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
                Turn study time into quality time. Create private rooms and stay productive together, no matter where you are.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Create Private Room <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </Button>
              </div>
              
              {/* Video Section */}
              <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full aspect-video object-cover"
                  >
                    <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                      <Play className="w-5 h-5 text-primary-foreground ml-1" />
                    </div>
                    <span className="text-foreground font-medium">See how it works</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                  Study Together, <span className="text-gradient">Anywhere</span>
                </h2>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  Everything you need to create the perfect study environment with friends
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {features.map((feature, index) => (
                  <Card 
                    key={index} 
                    className="text-center bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in"
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <CardHeader>
                      <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                        {feature.icon}
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">{feature.description}</CardDescription>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-6">
                Why Study With Friends <span className="text-gradient">Works</span>
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                The science is clear: studying together boosts productivity. <a href="/study-stream/why-studying-on-camera-helps/" className="text-primary hover:underline">Discover the research behind why studying on camera helps you get more done →</a>
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { title: "Stay Accountable", desc: "When your friends are studying too, you're more likely to stay focused and committed to your goals." },
                  { title: "Share the Journey", desc: "Celebrate wins together and support each other through challenges." },
                  { title: "Learn Together", desc: "Quick questions, shared resources, and collaborative problem-solving make studying more effective." },
                  { title: "Flexible Connection", desc: "Choose when to chat and when to focus. Everyone works at their own pace." },
                ].map((item, index) => (
                  <Card key={index} className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                    <h3 className="text-2xl font-semibold mb-4 text-foreground">{item.title}</h3>
                    <p className="text-muted-foreground text-lg">{item.desc}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-16">
                Get Started in <span className="text-gradient">3 Steps</span>
              </h2>
              <div className="space-y-8">
                {[
                  { step: "1", title: "Create Your Room", desc: "Set up a private study room with a custom name and invite code" },
                  { step: "2", title: "Invite Your Friends", desc: "Share your room code and wait for your study squad to join" },
                  { step: "3", title: "Study Together", desc: "Start your session and enjoy the motivation of studying with friends" },
                ].map((item, index) => (
                  <div key={index} className="flex gap-6 items-start animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                    <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center font-bold text-2xl text-primary-foreground glow-primary">
                      {item.step}
                    </div>
                    <div className="pt-2">
                      <h3 className="text-2xl font-semibold mb-2 text-foreground">{item.title}</h3>
                      <p className="text-muted-foreground text-lg">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Study Better <span className="text-gradient">Together</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Create your private study room now and invite your friends.
              </p>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Create Room Now
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StudyWithFriends;